import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { KeyedMutator } from "swr";
import {
  GetOrders200Response,
  InstrumentTypeResponse,
  OrderTypeResponse,
  SideResponse,
  SystemTypeResponse,
} from "../../codegen-api";
import { AuthContext } from "../AuthContext";
import { WebsocketContext } from "../WebsocketAuthContext";
import { APIEndpointEnum } from "../../enums/endpoint";
import { useInstrumentResolver } from "../../hooks/markets/useInstrumentResolver";
import { getMarketDecimals } from "../../utils/rawConversion";
import {
  clearScheduledRevalidations,
  scheduleRevalidations,
} from "../../utils/revalidation";
import { shouldRunRealtimeRevalidation } from "../../utils/realtimeRevalidation";
import { forEachFrame } from "../../hooks/wss/parseFrame";
import { toNanosString } from "../../utils/timestampUnits";
import { RealtimeOrder } from "./types";
import {
  classifyRealtimeStatus,
  forgetRealtimeOpenOrder,
  formatRawUnits,
  getConfiguredPerpMarketName,
  parseRealtimeMessage,
  reconcileOrdersSnapshot,
  rememberRealtimeEvent,
  rememberRealtimeOpenOrder,
  sortOrders,
} from "./helpers";

interface UseRealtimeOrdersParams {
  account: React.ContextType<typeof AuthContext>["account"];
  accountKey: string | undefined;
  authenticated: React.ContextType<typeof WebsocketContext>["authenticated"];
  triggerSubscribe: React.ContextType<typeof WebsocketContext>["triggerSubscribe"];
  triggerUnsubscribe: React.ContextType<typeof WebsocketContext>["triggerUnsubscribe"];
  registerRevalidate?: React.ContextType<typeof WebsocketContext>["registerRevalidate"];
  lastMessages: React.ContextType<typeof WebsocketContext>["lastMessages"];
  resolveMarket: ReturnType<typeof useInstrumentResolver>["resolveMarket"];
  mutateOrders: KeyedMutator<GetOrders200Response[]>;
  /** The orders in the SWR cache now; a snapshot equal to them is not written (FE-8). */
  orders?: GetOrders200Response[];
  revalidationTimersRef: MutableRefObject<number[]>;
  lastRevalidationRef: MutableRefObject<number>;
}

export function useRealtimeOrders({
  account,
  accountKey,
  authenticated,
  triggerSubscribe,
  triggerUnsubscribe,
  registerRevalidate,
  lastMessages,
  resolveMarket,
  mutateOrders,
  orders,
  revalidationTimersRef,
  lastRevalidationRef,
}: UseRealtimeOrdersParams) {
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  // Websocket-driven refresh. The global gate exists to damp a burst of
  // inbound `orders:` events into one revalidation, so it belongs here — and
  // ONLY here.
  const revalidateOrdersAfterRealtime = useCallback(() => {
    if (
      accountKey &&
      !shouldRunRealtimeRevalidation(`${APIEndpointEnum.ORDERS}:${accountKey}`, 8000)
    ) {
      return;
    }
    scheduleRevalidations(
      mutateOrders,
      revalidationTimersRef,
      lastRevalidationRef,
      {
        delays: [2500],
        throttleMs: 8000,
        includeImmediate: false,
      }
    );
  }, [accountKey, mutateOrders]);

  // Action-driven refresh, for a place / cancel / edit the user just performed.
  //
  // This deliberately does NOT consult `shouldRunRealtimeRevalidation`: that
  // gate is a module-global keyed only by endpoint + account, so it was shared
  // with the websocket path above. Any user action landing within 8s of an
  // inbound `orders:` event returned early and did nothing at all — not even
  // the immediate mutate — silently dropping the refresh for every spot
  // place/cancel and for createOrder / removeAllOrders.
  //
  // The timer refs are separate for the same reason: `scheduleRevalidations`
  // clears the ref it is handed, so sharing one would let a websocket message
  // cancel a pending action ladder (and vice versa).
  const actionTimersRef = useRef<number[]>([]);
  const lastActionRevalidationRef = useRef(0);
  const revalidateOrdersSoon = useCallback(
    (includeImmediate = true) => {
      scheduleRevalidations(
        mutateOrders,
        actionTimersRef,
        lastActionRevalidationRef,
        {
          delays: [2500],
          // Short enough that a second user action is never swallowed, long
          // enough that a rapid burst of cancels coalesces into one ladder.
          throttleMs: 1000,
          includeImmediate,
        }
      );
    },
    [mutateOrders]
  );

  useEffect(
    () => () => {
      clearScheduledRevalidations(revalidationTimersRef);
      clearScheduledRevalidations(actionTimersRef);
    },
    []
  );

  const accountOrderChannel = useMemo(
    () => (account ? `orders:${account.toLowerCase()}` : undefined),
    [account]
  );

  // Read when the server could not send the orders snapshot; a ref so a new
  // `mutateOrders` never re-subscribes.
  const revalidateOnResyncRef = useRef(revalidateOrdersAfterRealtime);
  revalidateOnResyncRef.current = revalidateOrdersAfterRealtime;

  useEffect(() => {
    if (!authenticated || !accountOrderChannel) {
      return undefined;
    }
    const channels = [accountOrderChannel];
    triggerSubscribe("subscribe", channels);
    const unregister = registerRevalidate?.(channels, () =>
      revalidateOnResyncRef.current()
    );
    // Release on unmount, account change or de-auth (M1).
    return () => {
      unregister?.();
      triggerUnsubscribe(channels);
    };
  }, [
    accountOrderChannel,
    authenticated,
    registerRevalidate,
    triggerSubscribe,
    triggerUnsubscribe,
  ]);

  /**
   * An open-orders row for a realtime order, or `undefined` when the order is
   * not KNOWN to be open: finished, unidentifiable, or carrying a status we do
   * not recognise. An unknown status is never guessed as "Opened" (FE-6).
   */
  const buildOrderFromRealtime = useCallback(
    (order?: RealtimeOrder) => {
      if (!order) return undefined;

      const disposition = classifyRealtimeStatus(order.status);
      if (disposition.kind !== "open") return undefined;

      const poolId = order.poolId || order.instrument_id;
      const orderId = order.orderId || order.order_id || order.id;
      if (!poolId || !orderId) return undefined;

      const configuredPerpName = getConfiguredPerpMarketName(poolId);
      const market = resolveMarket({
        instrument_id: poolId,
        instrument_name: configuredPerpName,
        instrument_type: configuredPerpName
          ? InstrumentTypeResponse.Perpetual
          : undefined,
      });
      const instrumentName = market?.instrument_name || configuredPerpName || poolId;
      const instrumentType =
        market?.instrument_type ||
        (configuredPerpName
          ? InstrumentTypeResponse.Perpetual
          : InstrumentTypeResponse.Spot);
      const isPerp = instrumentType === InstrumentTypeResponse.Perpetual;
      const decimals = isPerp
        ? { baseDecimals: 18, quoteDecimals: 18 }
        : getMarketDecimals(instrumentName || poolId);
      // A spot market whose decimals have not arrived cannot be scaled. Drop
      // the realtime row rather than render an open order at a guessed size or
      // price — the REST /orders poll still supplies it, correctly scaled, and
      // a wrong number on a live order is something a trader would act on.
      if (!decimals) return undefined;
      // Nanoseconds like REST rows, whatever unit the event carried (FE-16);
      // `Date.now()` milliseconds used to sort these rows as ancient.
      const timestamp =
        toNanosString(order.timestamp) ?? (toNanosString(Date.now()) as string);

      return {
        order_id: String(orderId),
        account: order.user || account || "",
        instrument_id: poolId,
        instrument_name: instrumentName,
        instrument_type: instrumentType,
        order_type:
          order.type?.toUpperCase() === "MARKET"
            ? OrderTypeResponse.Market
            : OrderTypeResponse.Limit,
        side:
          order.side?.toLowerCase() === "sell"
            ? SideResponse.Sell
            : SideResponse.Buy,
        amount: formatRawUnits(order.quantity, decimals.baseDecimals),
        price: formatRawUnits(order.price, decimals.quoteDecimals),
        avg_price: formatRawUnits(
          order.avgExecutionPrice || order.price,
          decimals.quoteDecimals
        ),
        filled: formatRawUnits(order.filled, decimals.baseDecimals),
        order_status: disposition.status,
        post_only: false,
        reduce_only: Boolean(order.reduceOnly ?? order.reduce_only ?? false),
        timestamp,
        created_timestamp: timestamp,
        system_type: SystemTypeResponse.Web,
      } as GetOrders200Response;
    },
    [account, resolveMarket]
  );

  useEffect(() => {
    if (!lastMessages || !accountOrderChannel) {
      return;
    }

    let shouldRevalidateOrders = false;
    const normalizedChannel = accountOrderChannel.toLowerCase();
    const normalizedAccount = account?.toLowerCase();

    forEachFrame(lastMessages, "useRealtimeOrders", (lastMessage) => {
      const parsed = parseRealtimeMessage(lastMessage.data);
      const channel = parsed?.channel;
      const data = parsed?.data;
      const dataRecord =
        !Array.isArray(data) && data && typeof data === "object"
          ? data
          : undefined;
      const order = dataRecord?.order;
      const realtimeAction = dataRecord?.action;
      const orderId = order?.orderId || order?.order_id || order?.id;
      const messageAccount =
        parsed?.account?.toLowerCase() ||
        dataRecord?.account?.toLowerCase();
      const snapshotOrders = Array.isArray(data)
        ? data
        : Array.isArray(dataRecord?.orders)
        ? dataRecord.orders
        : undefined;

      if (
        channel?.toLowerCase() === "orders" &&
        normalizedAccount &&
        messageAccount === normalizedAccount
      ) {
        if (snapshotOrders) {
          let hasUnknownRows = false;
          const nextOrders = snapshotOrders
            .map((snapshotOrder) => {
              const record =
                snapshotOrder && typeof snapshotOrder === "object"
                  ? (snapshotOrder as Record<string, unknown>)
                  : undefined;

              if (record?.order_id && record?.instrument_id) {
                return snapshotOrder as GetOrders200Response;
              }

              const realtimeOrder = snapshotOrder as RealtimeOrder | undefined;
              const disposition = classifyRealtimeStatus(realtimeOrder?.status);
              const rowId =
                realtimeOrder?.orderId || realtimeOrder?.order_id || realtimeOrder?.id;
              if (disposition.kind === "remove" && rowId) {
                forgetRealtimeOpenOrder(accountKey, String(rowId));
              }
              if (disposition.kind === "unknown") hasUnknownRows = true;
              return buildOrderFromRealtime(realtimeOrder);
            })
            .filter(Boolean) as GetOrders200Response[];

          const reconciled = reconcileOrdersSnapshot(
            accountKey,
            ordersRef.current,
            nextOrders
          );
          if (reconciled) {
            ordersRef.current = reconciled;
            mutateOrders(reconciled, { revalidate: false });
          }
          if (hasUnknownRows) shouldRevalidateOrders = true;
          return;
        }

        shouldRevalidateOrders = true;
        return;
      }

      if (
        !channel ||
        channel.toLowerCase() !== normalizedChannel ||
        !order ||
        !orderId
      ) {
        return;
      }

      const eventId = [
        channel,
        realtimeAction,
        orderId,
        order.transactionId,
        order.status,
        order.timestamp,
      ].join(":");
      if (!rememberRealtimeEvent(eventId)) {
        return;
      }

      const action = realtimeAction?.toLowerCase();
      const disposition = classifyRealtimeStatus(order.status);

      // Cancelled, filled, expired, rejected or failed: the row goes.
      if (action === "cancel" || disposition.kind === "remove") {
        forgetRealtimeOpenOrder(accountKey, String(orderId));
        shouldRevalidateOrders = true;
        mutateOrders(
          (current) =>
            current?.some((existing) => existing.order_id === String(orderId))
              ? current.filter((existing) => existing.order_id !== String(orderId))
              : current,
          { revalidate: false }
        );
        return;
      }

      // A status we do not know: ask the server rather than guess.
      if (disposition.kind === "unknown") {
        shouldRevalidateOrders = true;
        return;
      }

      const nextOrder = buildOrderFromRealtime(order);
      if (!nextOrder) {
        shouldRevalidateOrders = true;
        return;
      }

      rememberRealtimeOpenOrder(accountKey, nextOrder);
      mutateOrders(
        (current) =>
          sortOrders([
            nextOrder,
            ...(current || []).filter(
              (existing) => existing.order_id !== nextOrder.order_id
            ),
          ]),
        { revalidate: false }
      );
    });

    if (shouldRevalidateOrders) {
      revalidateOrdersAfterRealtime();
    }
  }, [
    account,
    accountKey,
    accountOrderChannel,
    buildOrderFromRealtime,
    lastMessages,
    mutateOrders,
    revalidateOrdersAfterRealtime,
  ]);

  return revalidateOrdersSoon;
}
